import { useEffect, useReducer, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { selectedCourseIdAtom } from "@src/features/libraryItem/course/atoms";
import {
  AGENT_RECENT_CHANGE_WINDOW_MS,
  AGENT_WORKING_SCREEN_MAX_MS,
  AgentActivityEntry,
  AgentActivityScope,
  courseAgentScope,
  selectScopeEntries,
  selectScopeWorking,
  useAgentActivityStore,
} from "./agentActivityStore";

type AgentActivity = {
  isRecentChange: boolean;
  latest: AgentActivityEntry | null;
};

/**
 * Scope for the course currently open in a course-builder view (standalone or
 * embedded in the module builder). Both builders hydrate courseVersionAtom
 * with the open course, so in-builder readers (screen indicators, the center
 * header pill) resolve their scope from it without prop-drilling.
 */
export const useCurrentCourseAgentScope = ():
  | AgentActivityScope
  | undefined => {
  const courseId = useAtomValue(selectedCourseIdAtom);
  return courseAgentScope(courseId);
};

export const useAgentActivity = (
  scope: AgentActivityScope | undefined,
): AgentActivity => {
  const latest = useAgentActivityStore((s) => {
    const entries = selectScopeEntries(scope)(s);
    return entries.length > 0 ? entries[entries.length - 1] : null;
  });
  const [isRecentChange, setIsRecentChange] = useState(false);

  useEffect(() => {
    if (!latest) {
      setIsRecentChange(false);
      return;
    }
    const remaining =
      AGENT_RECENT_CHANGE_WINDOW_MS - (Date.now() - latest.observedAt);
    if (remaining <= 0) {
      setIsRecentChange(false);
      return;
    }
    setIsRecentChange(true);
    const timer = setTimeout(() => setIsRecentChange(false), remaining);
    return () => clearTimeout(timer);
  }, [latest]);

  return { isRecentChange, latest };
};

export const useReportAgentWorking = (
  scope: AgentActivityScope | undefined,
  working: boolean | null,
): void => {
  const setWorking = useAgentActivityStore((s) => s.setWorking);
  useEffect(() => {
    if (!scope) return;
    setWorking(scope, working);
  }, [scope, working, setWorking]);
  useEffect(() => {
    if (!scope) return;
    return () => setWorking(scope, null);
  }, [scope, setWorking]);
};

export const useShowAgentActivity = (
  scope: AgentActivityScope | undefined,
): boolean => {
  const working = useAgentActivityStore(selectScopeWorking(scope));
  return working === true;
};

/** useShowAgentActivity for the course open in the current builder view. */
export const useShowCurrentCourseAgentActivity = (): boolean =>
  useShowAgentActivity(useCurrentCourseAgentScope());

/**
 * Whether a screen's text should animate in (typewriter). Animation is a
 * "watch the agent write it" affordance, so it runs at most ONCE per screen
 * per session, and only for text that appears while the user is looking:
 *
 * - `completedScreenIds` gates every path: once a screen's animate-in has
 *   finished it never re-animates — the backend `Prompt.aiGenerationInProgress`
 *   flag stays true on every already-written prompt until the whole course
 *   generation finalizes, so without this gate, switching module items away
 *   and back re-ran the typewriter on slides that had already played.
 * - A screen that mounts already holding its text with no live agent write
 *   observed this session was built before we arrived (navigating back to a
 *   mid-generation course, or opening it fresh): render it instantly and mark
 *   it done so the per-slide working indicator skips it too.
 * - Otherwise animate on a live agent write (workingScreens, gated on the
 *   current course actually generating) or on the backend flag (which also
 *   drives non-agent flows like SCORM conversion, so it is not gated on the
 *   agent-activity store).
 */
export const useScreenTextAnimateIn = (
  screenId: string,
  backendGenerating: boolean,
  hasText: boolean,
): boolean => {
  const showAgentActivity = useShowCurrentCourseAgentActivity();
  const workingScreenAt = useAgentActivityStore(
    (s) => s.workingScreens[screenId] ?? 0,
  );
  const isCompleted = useAgentActivityStore((s) =>
    s.completedScreenIds.has(screenId),
  );
  const completeWorkingScreen = useAgentActivityStore(
    (s) => s.completeWorkingScreen,
  );
  const [, forceTick] = useReducer((n: number) => n + 1, 0);

  const workingRecently =
    workingScreenAt > 0 &&
    Date.now() - workingScreenAt < AGENT_WORKING_SCREEN_MAX_MS;

  // Frozen at first render: text present with no live write observed means
  // the slide predates this view. Synchronous (not an effect) so the full
  // text renders on the very first frame instead of starting a typewriter.
  const prebuiltRef = useRef(hasText && !workingRecently);

  // Mark prebuilt slides done in the store so useSlideWorkingIndicator (the
  // per-slide "Building screen" treatment) skips them as well. Reactive to
  // backendGenerating (not mount-only): a slide can mount with its text while
  // the flag is still false and have the flag flip true on the same mount
  // (e.g. a background refetch) — without re-marking, the indicator would show
  // "building" with no typewriter run to ever complete it. The !workingRecently
  // guard keeps a genuinely re-worked slide animatable: a live agent write
  // clears the done status via setWorkingScreen and records recency, and we
  // must not immediately re-complete it out from under that animation.
  useEffect(() => {
    if (
      prebuiltRef.current &&
      backendGenerating &&
      !isCompleted &&
      !workingRecently
    ) {
      completeWorkingScreen(screenId);
    }
  }, [
    backendGenerating,
    isCompleted,
    workingRecently,
    screenId,
    completeWorkingScreen,
  ]);

  const isActive =
    !isCompleted &&
    ((showAgentActivity && workingRecently) ||
      (backendGenerating && !prebuiltRef.current));

  useEffect(() => {
    if (!isActive || !workingRecently) return;
    const remaining =
      AGENT_WORKING_SCREEN_MAX_MS - (Date.now() - workingScreenAt);
    const timer = setTimeout(forceTick, Math.max(0, remaining) + 1);
    return () => clearTimeout(timer);
  }, [isActive, workingRecently, workingScreenAt]);

  return isActive;
};

export const useCompleteWorkingScreen = (): ((screenId: string) => void) =>
  useAgentActivityStore((s) => s.completeWorkingScreen);

// Minimum time the working indicator stays visible, so instant changes (e.g. a
// media swap that lands in a split second) still register.
const MIN_VISIBLE_MS = 2500;

/**
 * Whether to show the per-slide working indicator. True while the agent is
 * working the slide — via a builder action (workingScreens) or the backend
 * prompt flag — ending when the typewriter completes (completedScreenId). Held
 * visible for a minimum so split-second changes are perceptible.
 */
export const useSlideWorkingIndicator = (
  screenId: string,
  promptGenerating: boolean,
): boolean => {
  const showAgentActivity = useShowCurrentCourseAgentActivity();
  const workingScreenAt = useAgentActivityStore(
    (s) => s.workingScreens[screenId] ?? 0,
  );
  const isCompleted = useAgentActivityStore((s) =>
    s.completedScreenIds.has(screenId),
  );

  const isWorking =
    showAgentActivity &&
    !isCompleted &&
    ((workingScreenAt > 0 &&
      Date.now() - workingScreenAt < AGENT_WORKING_SCREEN_MAX_MS) ||
      promptGenerating);

  const [visible, setVisible] = useState(isWorking);
  const shownAtRef = useRef(isWorking ? Date.now() : 0);

  useEffect(() => {
    if (isWorking) {
      if (!visible) shownAtRef.current = Date.now();
      setVisible(true);
      return;
    }
    if (!visible) return;
    const remaining = MIN_VISIBLE_MS - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => setVisible(false), remaining);
    return () => clearTimeout(timer);
  }, [isWorking, visible]);

  return visible;
};
